/**
 * Numa Portal — JS-side design tokens.
 *
 * The single source of truth for colors used in TS / JS (Chart.js datasets,
 * inline styles, JS-generated SVG, etc.). CSS-side tokens live in
 * src/styles/arcanum-theme.css under :root and stay in sync by name.
 *
 * The values are the Numa app's own tokens (zinc neutrals, brand purple,
 * the one status set), so the portal reads as part of Numa.
 */

export const ND_COLORS = {
  // Surfaces
  bg: '#FAFAFA',
  panel: '#FFFFFF',
  panel2: '#FAFAFA',
  panel3: '#F5F3F7',
  border: '#DCCBE6',

  // Text
  text: '#1D1724',
  textDim: '#463E50',
  textFaint: '#776E83',
  /** zinc-400: a mark with no status to show (dormant, nothing on record). */
  neutral: '#A1A1AA',
  /** The step between `good` and `warn` on a four-step scale. The lime already in the chart palette. */
  mid: '#84cc16',

  // Brand
  accent: '#8E50A7',
  accentLight: '#D7C2E0',
  accentDark: '#744188',
  accent2: '#0D9488',

  // States
  warn: '#d97706',
  bad: '#DC2626',
  good: '#16A34A',
  info: '#2563EB',
} as const;

/**
 * Chart series palette — leads with Numa purple, then varied accents
 * tuned for readability on a white card.
 */
export const ND_CHART_PAL: string[] = [
  '#8E50A7', // Numa purple
  '#0D9488', // Teal
  '#2563EB', // Blue
  '#d97706', // Warm amber
  '#16A34A', // Success green
  '#744188', // Dark purple
  '#DC2626', // Error red
  '#0EA5E9', // Sky blue
  '#84cc16', // Lime
  '#f97316', // Orange
  '#06b6d4', // Cyan
  '#a855f7', // Violet
  '#facc15', // Yellow
  '#fb7185', // Rose
  '#94a3b8', // Slate
];

/**
 * Soft fill versions of the brand colors for area / fill backgrounds.
 */
export const ND_FILL = {
  accent: 'rgba(142, 80, 167, 0.10)',
  accentStrong: 'rgba(142, 80, 167, 0.55)',
  accent2: 'rgba(13, 148, 136, 0.10)',
  good: 'rgba(22, 163, 74, 0.18)',
  warn: 'rgba(217, 119, 6, 0.18)',
} as const;
